import { useCallback, useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { isFuture, isPast } from "date-fns";
import { AxiosError } from "axios";

import { Dialog, TranslationAnchor } from "components/common";

import { parsePaymentMethod } from "utility/paymentMethod";
import {
  checkLoanInRange,
  extractError,
  fetchFromConfig,
  getDateFromExpression,
  getDefaultInputValue,
  getEnumByValue,
  PAYMENT_METHOD_TYPE,
  renderConditionalProp,
} from "utility";
import {
  ACH,
  CARD,
  paymentAmountType,
  recurrenceScheduleType,
} from "constants/common";

import { useInput, UseInput, useAddNotification } from "hooks";
import { useRootDispatch, useRootSelector } from "store";
import { useRequestDispatch } from "store/requestHooks";

import {
  activeAccountTypeConfig,
  currentAutoPay,
  getAccountRestrictions,
  getPaymentMethods,
  selectedAccountInfo,
} from "store/account/account-selector";
import {
  deleteAutoPayInfo,
  DeleteAutoPayInfo,
  saveAutoPayInfo,
  SaveAutoPayInfo,
} from "store/payments/payments-action";
import { AutoPayDTO } from "store/payments/payments-state";
import {
  clearSlide,
  setCurrentPaymentMethod,
  updateSlide,
} from "store/common/common-slice";
import { PaymentMethod } from "store/paymentMethods/paymentMethods-state";
import { selectAllPaymentMethods } from "store/paymentMethods/paymentMethods-slice";
import {
  fetchSchedulePayments,
  FetchSchedulePayments,
} from "store/schedulePayments/schedulePayments-action";

import classes from "./AutoPay.module.scss";
import { ScheduledPaymentsFilter } from "store/schedulePayments/schedulePayments-state";
import { Disclaimer } from "components/extra/disclaimer/Disclaimer";
import { isExpired } from "utility/utils";
import { MakePaymentClick } from "components/sliderPage/makePayment/MakePayment";
import {
  AddPaymentMethods,
  PaymentActionType,
} from "../addPaymentMethods/AddPaymentMethods";

export interface AutoPayProps {}

export const AutoPay: React.FC<AutoPayProps> = () => {
  const { t } = useTranslation([
    "common",
    "payment",
    "payment_next",
    "payment_final",
  ]);
  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();
  const addNotification = useAddNotification();

  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [showDisabledDialog, setShowDisabledDialog] = useState<boolean>(false);
  const [buttonClick, setbuttonClick] = useState<MakePaymentClick>({
    count: 0,
    scheduleFlag: false,
  });
  const updateButtonClickFromChild = useCallback(
    (updateFn: (prev: MakePaymentClick) => MakePaymentClick) => {
      setbuttonClick((prev) => {
        const newValue = updateFn(prev);
        return newValue;
      });
    },
    [],
  );

  const { meta } = useRootSelector((state) => state.payments);
  const autoPayInfo = useRootSelector(currentAutoPay);
  const accountInfo = useRootSelector(selectedAccountInfo);
  const { restrictions } = useRootSelector(getAccountRestrictions);

  const paymentMethods = useRootSelector(selectAllPaymentMethods);
  const paymentMethodTypesEnabled = useRootSelector(getPaymentMethods);
  const user = useRootSelector((state) => state.user.user);
  const currentPaymentMethod = useRootSelector(
    (state) => state.common.currentPaymentMethod,
  );
  const clientConfig = useRootSelector((state) => state.config.client);

  const config = useRootSelector(activeAccountTypeConfig);
  const currentPaymentConfig =
    paymentMethodTypesEnabled[currentPaymentMethod || ACH];

  const [disableFlag, setDisableFlag] = useState(false);

  const showUpdatedPopUp =
    accountInfo?.amountDue! > 0 && isPast(new Date(accountInfo?.dueDate!));

  const amountDueInRange = checkLoanInRange(
    accountInfo?.amountDue,
    currentPaymentConfig,
  );
  const otherAmountDueInRange =
    !!accountInfo?.otherAmountDue &&
    checkLoanInRange(accountInfo?.otherAmountDue, currentPaymentConfig);

  const otherAmount: UseInput = useInput(
    getDefaultInputValue(autoPayInfo?.paymentAmount, ""),
  );

  const amountType: UseInput = useInput(
    getDefaultInputValue(
      (() => {
        if (!autoPayInfo?.paymentAmountType) {
          if (amountDueInRange) {
            return paymentAmountType.due_amount;
          } else if (otherAmountDueInRange) {
            return paymentAmountType.other_due_amount;
          } else {
            return paymentAmountType.other;
          }
        } else {
          return autoPayInfo?.paymentAmountType;
        }
      })(),
    ),
  );

  const dateToBePaidType: UseInput = useInput(
    getDefaultInputValue(
      autoPayInfo?.recurrenceScheduleType,
      recurrenceScheduleType.due_date,
    ),
  );

  const paymentType: UseInput = useInput(
    getDefaultInputValue(fetchFromConfig(config, "initialPaymentTypes")),
  );

  const scheduleDate: UseInput = useInput(
    getDefaultInputValue(
      autoPayInfo?.scheduleExpression
        ? getDateFromExpression(autoPayInfo.scheduleExpression)
        : "1",
      "1",
    ),
  );

  const paymentMethod: UseInput = useInput(
    getDefaultInputValue(
      autoPayInfo?.paymentTypeCode
        ? autoPayInfo.paymentMethodId
        : paymentMethods && paymentMethods[0]
          ? paymentMethods[0].id
          : 0,
      0,
    ),
  );

  const paymentMethodSelection: UseInput = useInput(
    getDefaultInputValue("saved_method", "saved_method"),
  );

  const cancelButtonHandler = () => {
    dispatch(clearSlide());
  };

  const requestHandler = (currentMethod?: PaymentMethod) => {
    setbuttonClick({
      count: 0,
      scheduleFlag: dateToBePaidType.value === "now" ? false : true,
    });

    if (
      +amountType.value === paymentAmountType.other &&
      !otherAmount.ref.current.checkValidity()
    ) {
      otherAmount.ref.current.reportValidity();
      return null;
    }

    const autoRequestDTO = {
      recurrenceScheduleType: parseInt(dateToBePaidType.value),
      scheduleExpression: `0 0 ${scheduleDate.value} * *`,
      userId: user?.id,
      clientId: user?.clientId ? user.clientId : 0,
      accountInformationId: accountInfo?.id,
      paymentMethodId: currentMethod?.id ?? parseInt(paymentMethod.value),
      paymentAmountType: parseInt(amountType.value),
      paymentAmount: parseFloat(otherAmount.value) || null,
      paymentTypeCode: paymentType.value,
      firstScheduledPaymentDate:
        +dateToBePaidType.value === recurrenceScheduleType.due_date &&
        isFuture(new Date(accountInfo?.dueDate!))
          ? accountInfo?.dueDate
          : null,
      firstPaymentAmount:
        +amountType.value === paymentAmountType.due_amount &&
        isFuture(new Date(accountInfo?.dueDate!))
          ? accountInfo?.amountDue
          : null,
      id: autoPayInfo?.id,
    };

    if (autoRequestDTO) {
      actionPromiseHandler<SaveAutoPayInfo, AutoPayDTO>(
        saveAutoPayInfo,
        autoRequestDTO as AutoPayDTO,
      );
    }
  };

  const actionPromiseHandler = <T extends Function, U>(action: T, data: U) => {
    requestDispatch<T, U>(action, data)
      .unwrap()
      .then(() => {
        requestDispatch<FetchSchedulePayments, ScheduledPaymentsFilter>(
          fetchSchedulePayments,
          {
            accountInformationId: accountInfo?.id!,
            status: [0, 1],
          },
        );
        setShowSuccessDialog(true);
      })
      .catch((e: AxiosError) => {
        const errKey = extractError(e, "no_data_found");
        addNotification({
          messages: errKey,
          type: "ERROR",
          closable: true,
        });
      });
  };

  const saveChanges = async () => {
    if (disableFlag && accountInfo) {
      if (
        paymentMethodSelection.value === "saved_method" &&
        !paymentMethod.ref.current.checkValidity()
      ) {
        paymentMethod.ref.current.reportValidity();
        return;
      }

      if (paymentMethodSelection.value !== "saved_method") {
        setbuttonClick((prev) => ({
          count: prev.count + 1,
          scheduleFlag: dateToBePaidType.value === "now" ? false : true,
        }));
      } else {
        requestHandler();
      }
    } else {
      setShowDisabledDialog(true);
    }
  };

  const toggleHandler = () => {
    if (accountInfo?.active || disableFlag) {
      setDisableFlag(!disableFlag);
    }
  };

  useEffect(() => {
    setbuttonClick({
      count: buttonClick.count,
      scheduleFlag: dateToBePaidType.value === "now" ? false : true,
    });
  }, [dateToBePaidType.value, buttonClick.count]);

  useEffect(() => {
    if (
      autoPayInfo?.status === 1 &&
      autoPayInfo.accountInformationId === accountInfo?.id
    )
      setDisableFlag(true);
  }, [autoPayInfo?.status, autoPayInfo?.accountInformationId, accountInfo?.id]);

  const getCurrentMethod = () => {
    const currentMethod = paymentMethods.find(
      (method) => method.id === parseInt(paymentMethod.value),
    );
    return currentMethod;
  };

  useEffect(() => {
    const currentMethod = getCurrentMethod();
    if (
      currentMethod != null &&
      currentMethod.paymentMethodName !== undefined
    ) {
      dispatch(
        setCurrentPaymentMethod(
          getEnumByValue(PAYMENT_METHOD_TYPE, currentMethod.paymentMethodType!),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod.value]);

  const renderPaymentMethods = (paymentMethods: PaymentMethod[]) => {
    return paymentMethods
      .filter((single) => !isExpired(single.expirationDate))
      .map((single, i) => {
        const { title } = parsePaymentMethod(single);
        const methodType = getEnumByValue(
          PAYMENT_METHOD_TYPE,
          single.paymentMethodType!,
        );
        let paymentTypeRestricted = false;
        switch (methodType) {
          case ACH:
            paymentTypeRestricted = restrictions.achRestricted;
            break;
          case CARD:
            paymentTypeRestricted = restrictions.cardRestricted;
            break;
          default:
            break;
        }

        if (
          methodType !== null &&
          paymentMethodTypesEnabled[methodType] &&
          !paymentTypeRestricted
        ) {
          return (
            <ft-list-item
              key={i}
              {...renderConditionalProp(
                paymentMethod.value === single.id,
                "selected",
              )}
              value={single.id}
            >
              {title}
            </ft-list-item>
          );
        }
        return null;
      });
  };

  const renderDates = () => {
    let res = [];
    for (let i = 1; i <= 28; i++) {
      res.push(
        <ft-list-item
          key={i}
          {...renderConditionalProp(
            scheduleDate.value === i.toString(),
            "selected",
          )}
          value={i}
        >
          {i}
        </ft-list-item>,
      );
    }
    return res;
  };

  const renderPaymentType = () => {
    const items: JSX.Element[] = [];
    config.paymentTypes.forEach((paymentTypeConfig) => {
      const item = (
        <ft-list-item
          key={paymentTypeConfig.code}
          {...renderConditionalProp(
            paymentType.value === paymentTypeConfig.code,
            "selected",
          )}
          value={paymentTypeConfig.code}
        >
          {paymentTypeConfig.name}
        </ft-list-item>
      );
      items.push(item);
    });
    return items;
  };

  const disableAutoPay = async () => {
    if (autoPayInfo) {
      actionPromiseHandler<SaveAutoPayInfo, AutoPayDTO>(saveAutoPayInfo, {
        ...autoPayInfo,
        status: 2,
      } as AutoPayDTO);
    }
  };

  const deleteAutoPay = async () => {
    if (autoPayInfo) {
      actionPromiseHandler<DeleteAutoPayInfo, number>(
        deleteAutoPayInfo,
        autoPayInfo.id,
      );
    }
  };

  const mainClass = [classes.AutoPayWrapper];
  if (!disableFlag) {
    mainClass.push(classes.Disabled);
  }

  function getFormattedDate(autoPayInfo: AutoPayDTO | undefined): string {
    if (autoPayInfo?.modified) {
      return autoPayInfo.modified.toString();
    } else if (autoPayInfo?.created) {
      return autoPayInfo.created.toString();
    } else {
      return new Date().toISOString();
    }
  }

  const getDisplayPaymentAmount = () => {
    if (+amountType.value === paymentAmountType.due_amount) {
      return accountInfo?.amountDue;
    } else if (+amountType.value === paymentAmountType.other_due_amount) {
      return accountInfo?.otherAmountDue;
    } else if (+amountType.value === paymentAmountType.other) {
      return parseFloat(otherAmount.value) || 0;
    }
    return undefined;
  };

  return (
    <div className={mainClass.join(" ")}>
      <div className={classes.ToggleWrapper}>
        <p className={classes.Heading}>{t("common:autopay_label")}</p>
        <ft-switch
          aria-label="auto pay switch button"
          data-testid="autopay-toggle"
          onClick={toggleHandler}
          {...renderConditionalProp(
            accountInfo?.active === true && disableFlag,
            "selected",
          )}
        ></ft-switch>
      </div>
      <p className={classes.AutoPayHeading}>
        {t("payment_next:autopay_information")}
      </p>
      {accountInfo ? (
        <>
          <div className={classes.SectionWrapper}>
            <p className={classes.Heading}>
              {t("payment_final:payment_amount")}
            </p>
            {currentPaymentConfig.payments.amountSelectionOption.includes(
              "amount-due",
            ) && (
              <>
                <ft-formfield label={`${t("common:amount_due")}`}>
                  <ft-radio
                    {...renderConditionalProp(
                      +amountType.value === paymentAmountType.due_amount,
                      "checked",
                    )}
                    onClick={amountType.valueChangeHandler}
                    name="amount-type"
                    value={paymentAmountType.due_amount}
                    {...renderConditionalProp(!disableFlag, "disabled")}
                    data-testid="autopay-page-amount-due"
                  ></ft-radio>
                </ft-formfield>
                {!!accountInfo.otherAmountDue && (
                  <ft-formfield label={`${t("common:other_amount_due")}`}>
                    <ft-radio
                      {...renderConditionalProp(
                        +amountType.value ===
                          paymentAmountType.other_due_amount,
                        "checked",
                      )}
                      onClick={amountType.valueChangeHandler}
                      name="amount-type"
                      value={paymentAmountType.other_due_amount}
                      {...renderConditionalProp(!disableFlag, "disabled")}
                      data-testid="autopay-page-other-amount"
                    ></ft-radio>
                  </ft-formfield>
                )}
              </>
            )}
            {currentPaymentConfig.payments.amountSelectionOption.includes(
              "user-provided",
            ) && (
              <>
                <ft-formfield label={`${t("payment_next:other_amount")} $:`}>
                  <ft-radio
                    {...renderConditionalProp(
                      +amountType.value === paymentAmountType.other,
                      "checked",
                    )}
                    onClick={amountType.valueChangeHandler}
                    name="amount-type"
                    value={paymentAmountType.other}
                    {...renderConditionalProp(!disableFlag, "disabled")}
                  ></ft-radio>
                </ft-formfield>
                <ft-textfield
                  class={classes.PaymentAmount_amount}
                  outlined
                  label={t("payment:amount")}
                  customInput="test"
                  type={"number"}
                  step={".01"}
                  min={currentPaymentConfig.payments.minimumPaymentAmount}
                  max={currentPaymentConfig.payments.maximumPaymentAmount}
                  ref={otherAmount.ref}
                  validationMessage={t("payment_next:greater_than_zero", {
                    min: currentPaymentConfig.payments.minimumPaymentAmount,
                    max: currentPaymentConfig.payments.maximumPaymentAmount,
                  })}
                  onInput={otherAmount.decimalValueHandler}
                  value={otherAmount.value}
                  onBlur={otherAmount.inputBlurHandler}
                  {...renderConditionalProp(
                    +amountType.value === paymentAmountType.other,
                    "required",
                  )}
                  icon="attach_money"
                  {...renderConditionalProp(
                    +amountType.value !== paymentAmountType.other,
                    "disabled",
                  )}
                  onWheel={(e: WheelEvent) =>
                    e.target instanceof HTMLElement && e.target.blur()
                  }
                ></ft-textfield>
              </>
            )}
          </div>
          {Object.keys(config.paymentTypes).length === 1 ? null : (
            <div className={classes.SectionWrapper}>
              <p className={classes.Heading}>
                {t("payment_next:payment_type")}
              </p>
              <ft-select
                validationMessage=" "
                onClick={paymentType.valueChangeHandler}
                value={paymentType.value}
                onBlur={paymentType.inputBlurHandler}
                outlined
                label={t("payment_next:payment_type")}
                id="payment-type"
                class={classes.PaymentTypeSelect}
                {...renderConditionalProp(!disableFlag, "disabled")}
              >
                {renderPaymentType()}
              </ft-select>
            </div>
          )}
          <div className={classes.SectionWrapper}>
            <p className={classes.Heading}>
              {t("payment_next:date_to_be_paid")}
            </p>
            <ft-formfield label={t("common:due_date")}>
              <ft-radio
                {...renderConditionalProp(
                  +dateToBePaidType.value === recurrenceScheduleType.due_date,
                  "checked",
                )}
                onClick={dateToBePaidType.valueChangeHandler}
                name="date-to-be-paid-type"
                value={recurrenceScheduleType.due_date}
                {...renderConditionalProp(!disableFlag, "disabled")}
              ></ft-radio>
            </ft-formfield>
            {currentPaymentConfig?.scheduledPayments.enabled && (
              <>
                <ft-formfield label={t("payment_next:select_custom_date")}>
                  <ft-radio
                    {...renderConditionalProp(
                      +dateToBePaidType.value === recurrenceScheduleType.custom,
                      "checked",
                    )}
                    onClick={dateToBePaidType.valueChangeHandler}
                    name="date-to-be-paid-type"
                    value={recurrenceScheduleType.custom}
                    {...renderConditionalProp(!disableFlag, "disabled")}
                  ></ft-radio>
                </ft-formfield>
                <ft-select
                  validationMessage=" "
                  onClick={scheduleDate.valueChangeHandler}
                  value={scheduleDate.value}
                  onBlur={scheduleDate.inputBlurHandler}
                  outlined
                  label={t("payment_next:select_date")}
                  id="select-date"
                  {...renderConditionalProp(
                    +dateToBePaidType.value === recurrenceScheduleType.custom
                      ? false
                      : true,
                    "disabled",
                  )}
                  class={classes.SavedPaymentMethod}
                >
                  {renderDates()}
                </ft-select>
              </>
            )}
          </div>
          <div className={classes.SectionWrapper}>
            <p className={classes.Heading}>
              {t("payment_next:choose_payment_method")}
            </p>
            <span className={classes.DialogLine}>
              {t("payment_next:processing_fee")}
            </span>
            {paymentMethods && paymentMethods[0] ? (
              <>
                <ft-formfield label={`${t("common:saved_payment_methods")}`}>
                  <ft-radio
                    {...renderConditionalProp(
                      paymentMethodSelection.value === "saved_method",
                      "checked",
                    )}
                    onClick={paymentMethodSelection.valueChangeHandler}
                    name="payment-method-selection"
                    value="saved_method"
                    data-testid="make-payment-page-saved-payment-method"
                    {...renderConditionalProp(!disableFlag, "disabled")}
                  ></ft-radio>
                </ft-formfield>
                {paymentMethodSelection.value === "saved_method" && (
                  <ft-select
                    ref={paymentMethod.ref}
                    validationMessage={t("common:field_not_empty")}
                    required
                    onClick={paymentMethod.valueChangeHandler}
                    value={paymentMethod.value}
                    onBlur={paymentMethod.inputBlurHandler}
                    outlined
                    label={t("payment_next:choose_payment_method")}
                    id="payment-method"
                    class={classes.SavedPaymentMethod}
                    {...renderConditionalProp(!disableFlag, "disabled")}
                  >
                    {paymentMethods
                      ? renderPaymentMethods(paymentMethods)
                      : null}
                  </ft-select>
                )}
              </>
            ) : null}
          </div>
          <ft-formfield label={`${t("payment_next:new_payment_method")}`}>
            <ft-radio
              {...renderConditionalProp(
                paymentMethodSelection.value === "new_method",
                "checked",
              )}
              onClick={paymentMethodSelection.valueChangeHandler}
              name="payment-method-selection"
              value="new_method"
              data-testid="make-payment-page-new-payment-method"
              {...renderConditionalProp(!disableFlag, "disabled")}
            ></ft-radio>
          </ft-formfield>
          {paymentMethodSelection.value === "new_method" && (
            <AddPaymentMethods
              buttonClick={buttonClick}
              updateButtonClick={updateButtonClickFromChild}
              paymentAmount={getDisplayPaymentAmount()}
              paymentHandler={requestHandler}
              inlineView={true}
              paymentActionType={PaymentActionType.SAVE_AND_PAY_LATER}
            />
          )}
          <ft-divider class={classes.Divider}></ft-divider>
          <div className={classes.SectionWrapper}>
            {currentPaymentMethod ? (
              <>
                <p className={classes.AuthorizationAndRevocation}>
                  <Trans
                    i18nKey={`payment_next:auth-and-revo_${currentPaymentMethod}_auth`}
                    values={{
                      clientName: clientConfig.name,
                    }}
                    components={{ a: <TranslationAnchor /> }}
                  />
                </p>
                <p className={classes.AuthorizationAndRevocation}>
                  <Trans
                    i18nKey={`payment_next:auth-and-revo_${currentPaymentMethod}_revo`}
                    values={{
                      todaysDate: t("common:date_format", {
                        value: getFormattedDate(autoPayInfo),
                      }),
                      contactPhone: clientConfig.primaryContactPhone,
                    }}
                    components={{ a: <TranslationAnchor /> }}
                  />
                </p>
                <ft-divider class={classes.Divider}></ft-divider>
              </>
            ) : (
              <></>
            )}
          </div>
          <Disclaimer
            translatedText={
              <Trans
                i18nKey="payment:autopay-disclaimer"
                components={{ a: <TranslationAnchor /> }}
              />
            }
          />
          <div className={classes.Buttons}>
            <ft-button
              class={[classes.Back, "outline-button"].join(" ")}
              label={t("common:cancel")}
              outlined
              onClick={cancelButtonHandler}
            ></ft-button>
            <ft-button
              class={classes.Make_payment}
              data-testid="save-button-autopay"
              label={t("common:save")}
              unelevated
              onClick={saveChanges}
              {...renderConditionalProp(
                (!disableFlag && !autoPayInfo) ||
                  ((paymentMethods && paymentMethods.length > 0) ||
                  paymentMethodSelection.value !== "saved_method"
                    ? false
                    : true),
                "disabled",
              )}
            ></ft-button>
          </div>
        </>
      ) : null}
      {showSuccessDialog ? (
        <Dialog
          showSecondaryAction={showUpdatedPopUp}
          icon="sync"
          primaryActionLabel={
            showUpdatedPopUp ? t("common:make_payment_label") : t("common:done")
          }
          secondaryActionLabel={showUpdatedPopUp ? t("common:done") : undefined}
          dialogClosed={() => {
            setShowSuccessDialog(false);
          }}
          secondaryActionHandler={() => dispatch(clearSlide())}
          primaryActionHandler={() => {
            showUpdatedPopUp
              ? dispatch(updateSlide({ id: "MakeAPayment" }))
              : dispatch(clearSlide());
          }}
          type="default"
          closeIcon
        >
          {showUpdatedPopUp
            ? t("payment_next:autopay_confirm_when_due_amount")
            : t("payment_next:changes_saved_successfully")}
        </Dialog>
      ) : null}
      {showDisabledDialog ? (
        <Dialog
          icon="sync"
          primaryActionLabel={t("common:yes_label")}
          secondaryActionLabel={t("common:no_label")}
          primaryActionHandler={disableAutoPay}
          secondaryActionHandler={deleteAutoPay}
          dialogClosed={() => {
            setShowDisabledDialog(false);
          }}
          type="default"
          closeIcon
          showSecondaryAction={true}
          scrimClickAction
        >
          <p className={classes.DialogLine1}>
            {t("payment_next:autopay_disabled")}
          </p>
          <p className={classes.DialogLine}>
            {t("payment_next:autopay_settings_save")}
          </p>
        </Dialog>
      ) : null}
      {/*       <div className={classes.FooterContainer}>
        {t("makepayment:submit_auth")}
      </div> */}
      {meta.isLoading && <ft-screen-loader></ft-screen-loader>}
    </div>
  );
};
